import Moment from 'moment';
import {
  DATE_FORMAT,
  DATE_FORMATS,
  toMoment,
} from 'utils/normalizers';

export const dateRegExps = [/^\d{4}-\d{2}-\d{2}$/, /^\d{2}-\d{2}-\d{4}$/];
export const fullDateRegExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(.\d+)-\d{2}:\d{2}$/;
export const dateAmericaMazatlanRegExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{6}-\d{2}:\d{2}/;
export const dateTimeArizonaTimeRegexExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{6}/;

const isJSON = (val) => {
  try {
    const parsed = JSON.parse(val);
    return parsed;
  } catch {
    return false;
  }
};

export const formatReceivedValues = (values = {}) => {
  const keys = Object.keys(values);
  const clone = {};
  keys.forEach((prop) => {
    const value = values[prop];
    clone[prop] = value;
    // check to discart falsy values
    if ((value === null || value === undefined || value === '')) {
      delete clone[prop];
    }
    if (prop === 'de_las' || prop === 'a_las') {
      clone[prop] = Moment(value, 'HH:mm:ss');
    }
    if (prop === 'rango_hora_inicial' || prop === 'rango_hora_final') {
      clone[prop] = Moment(value, 'HH:mm:ss');
    }
    if ((prop === 'horario_de_entrada' || prop === 'horario_de_salida') && value) {
      clone[prop] = Moment(value, 'HH:mm:ss');
    }
    // Los arreglos (ej. dias_que_labora/dias_que_no_labora) se excluyen de las
    // conversiones de fecha: un arreglo de un solo elemento se transforma a
    // string igual a su elemento (sin comas), calzando por error con estos
    // regex y rompiendo el arreglo al convertirlo en un solo Moment.
    if (!Array.isArray(value)) {
      const regexIdx = dateRegExps.findIndex((e) => e.test(value));
      if (regexIdx !== -1) {
        clone[prop] = new Moment(value, [DATE_FORMATS[regexIdx]]);
      }
      if (fullDateRegExp.test(value)) {
        clone[prop] = new Moment(new Moment(value).local().format());
      }
      if (dateAmericaMazatlanRegExp.test(value)) {
        clone[prop] = new Moment(value, [DATE_FORMAT]);
      }
      if (dateTimeArizonaTimeRegexExp.test(value)) {
        clone[prop] = new Moment(value, [DATE_FORMAT]);
      }
    }

    if ((prop === 'direccion' || prop === 'domicilio')) {
      const parsedValue = isJSON(value);
      if (/^[0-9]$/.test(value)) {
        clone[prop] = value;
      } else if (parsedValue) {
        clone[prop] = parsedValue;
      }
    }
  });
  clone.key = clone.id;
  return clone;
};

export const formatReceived = (values) => {
  if (Array.isArray(values)) {
    return values.map((e) => formatReceivedValues(e));
  }
  return formatReceivedValues(values);
};

export default (values, options = {}, fullDate = false) => {
  const {
    files = [],
    formData: asFormData,
    clean = true,
  } = options;
  const keys = Object.keys(values);
  const clone = {};
  keys.forEach((prop) => {
    const value = values[prop];
    clone[prop] = value;
    // check to discart falsy values
    if (value === null || value === undefined || value === '') {
      if (clean) {
        delete clone[prop];
      } else {
        clone[prop] = null;
      }
    }

    // check if the values is a date, only in get
    // Los arreglos (ej. dias_que_labora/dias_que_no_labora) se excluyen: un
    // arreglo de un solo elemento se convierte a string igual a su elemento
    // (sin comas) al testear el regex, calzando por error y rompiendo el
    // arreglo al convertirlo en un solo Moment (ver mismo fix en
    // formatReceivedValues más arriba).
    if (!Array.isArray(value)
      && (value instanceof Date || dateRegExps.some((e) => e.test(value)))) {
      clone[prop] = toMoment(value);
    }

    // check if the value is a Moment instance (ya sea porque vino como tal,
    // o porque se acaba de convertir arriba desde un string/Date), only if not get
    if (Moment.isMoment(clone[prop])) {
      clone[prop] = clone[prop]?.format(DATE_FORMAT);
    }
    if (Moment.isMoment(clone[prop]) && fullDate) {
      clone[prop] = clone[prop]?.format(`${DATE_FORMAT}[T]HH:mm:ss[Z]`);
    }

    // checking if the value is a instance of file
    // It also now checks if it is already an URL string from previous save.
    if (files.includes(prop)) {
      if (value?.file instanceof File) {
        clone[prop] = value.file;
      } else if (typeof value === 'string') {
        delete clone[prop];
      } else if (!asFormData && !clean) {
        clone[prop] = null;
      } else {
        delete clone[prop];
      }
    }

    // checking if the value is a address object in JSON
    if (['direccion', 'domicilio', 'domicilio_fiscal'].includes(prop)) {
      const parsedValue = isJSON(value);
      if (/^[0-9]$/.test(value)) {
        clone[prop] = value;
      } else if (parsedValue) {
        clone[prop] = parsedValue;
      }
    }
  });
  if (asFormData) {
    const formData = new FormData();
    const valuesKeys = Object.keys(clone);
    valuesKeys.forEach((key) => {
      const value = clone[key] !== null ? clone[key] : '';
      if (Array.isArray(value)) {
        // Cada elemento se añade por separado para que Django reciba PKs individuales,
        // no el string "1,2,3" que genera un append directo del array.
        value.forEach((item) => formData.append(key, item));
      } else {
        formData.append(`${key}`, value);
      }
    });
    return formData;
  }
  return clone;
};

export const discartNullValues = (values = {}, config = {}) => {
  const {
    numbers = false,
  } = config;
  const keys = Object.keys(values);
  const clone = {};
  keys.forEach((prop) => {
    const value = values[prop];
    if (value !== null && value !== 'N/A' && value !== '') {
      clone[prop] = value;
    }
    if (numbers) {
      if (/^(\d+)[.]?(\d+)?$/.test(value)) {
        clone[prop] = parseFloat(value) >= 0 ? parseFloat(value) : null;
      } else {
        clone[prop] = null;
      }
    }
  });
  return clone;
};

export const hasFiles = (values = {}, files = []) => files
  .some((prop) => values[prop] && typeof values[prop] !== 'string');
